import { useEffect, useRef, useState } from "react";

/** Centre mark — the Leaply logomark (same asset as the favicon / page
 *  brand). SVG: crisp at any orb size. */
const CENTER_LOGO_SRC = "/logomark.svg";

interface Props {
  active: boolean;
  level: number;
  onClick?: () => void;
  ariaLabel?: string;
  disabled?: boolean;
  /** Compact decorative variant — used by InterviewHero. Skips the
   *  audio-reactive pulse ring and uses tighter responsive widths. */
  compact?: boolean;
}

/**
 * The central audio-reactive orb (architecture §5.1's visual centrepiece).
 * Two counter-spinning conic-gradient layers with heavy blur create the
 * swirly green look; the entire orb subtly scales with the level meter.
 * Click anywhere on it to begin/end the call when `onClick` is set.
 *
 * Centre logo is the Leaply icon (`/leaply-icon.png` — same as the favicon),
 * with an inline gradient-sphere SVG as the onError fallback so there's no
 * broken-image flash if the asset is ever missing.
 */
export function Orb({
  active,
  level,
  onClick,
  ariaLabel,
  disabled,
  compact = false,
}: Props) {
  const scale = 1 + Math.min(level, 1) * 0.08;
  const logoScale = 1 + Math.min(level, 1) * 0.06;

  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(420);
  const [usePngLogo, setUsePngLogo] = useState(true);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setSize(w);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const ringSize = size * 0.81 + level * 0.14 * size;
  const logoSize = Math.round(size * 0.43);

  const clickable = Boolean(onClick) && !disabled;

  return (
    <div
      ref={ref}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? ariaLabel : undefined}
      aria-disabled={disabled || undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={`relative flex items-center justify-center aspect-square outline-none ${
        compact
          ? "w-[min(66vw,300px)] sm:w-[460px]"
          : // vh caps keep the orb from eating the whole screen on short
            // viewports (laptops with lots of browser chrome, the Slack
            // in-app browser): the orb shrinks so the caption + Begin button
            // always stay above the fold. On tall screens the px cap wins.
            "w-[min(68vw,44vh,320px)] sm:w-[min(70vw,50vh,456px)] lg:w-[min(52vh,504px)]"
      } ${
        clickable
          ? "cursor-pointer active:scale-[0.985] transition-transform focus-visible:ring-4 focus-visible:ring-brand-200 rounded-full"
          : ""
      } ${disabled ? "opacity-70 cursor-wait" : ""}`}
    >
      <div
        className="absolute inset-0 rounded-full border border-brand-100 shadow-orb bg-surface"
        style={{
          transform: `scale(${scale})`,
          transition: "transform 120ms ease-out",
        }}
      />

      <div
        className="absolute inset-[4%] rounded-full overflow-hidden"
        style={{
          transform: `scale(${scale})`,
          transition: "transform 120ms ease-out",
        }}
      >
        <div
          className="absolute inset-0 animate-spin-slow"
          style={{
            background:
              "conic-gradient(from 0deg, #c8e4d2 0deg, #ffffff 30deg, #2f8a59 75deg, #ffffff 130deg, #5fa97f 200deg, #ffffff 250deg, #0e5a37 310deg, #ffffff 360deg)",
            filter: "blur(28px)",
          }}
        />
        <div
          className="absolute inset-0 animate-spin-reverse"
          style={{
            background:
              "conic-gradient(from 180deg, transparent 0deg, #1c6e44 60deg, transparent 120deg, #5fa97f 180deg, transparent 240deg, #0a4329 300deg, transparent 360deg)",
            filter: "blur(34px)",
            opacity: 0.65,
          }}
        />
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0) 60%)",
          }}
        />
      </div>

      {active && !compact && (
        <div
          className="absolute rounded-full ring-2 ring-brand-700/10 animate-pulse-soft"
          style={{
            width: `${ringSize}px`,
            height: `${ringSize}px`,
            transition: "width 120ms ease-out, height 120ms ease-out",
          }}
        />
      )}

      {/* Centre logo — sits above the gradient layers, gently scales with
          audio. Leaply icon by default; gradient sphere if it fails to load. */}
      <div
        className="relative z-10 flex items-center justify-center"
        style={{
          width: logoSize,
          height: logoSize,
          transform: `scale(${logoScale})`,
          transition: "transform 120ms ease-out",
          filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.25))",
        }}
      >
        {usePngLogo ? (
          <img
            src={CENTER_LOGO_SRC}
            alt=""
            aria-hidden
            width={logoSize}
            height={logoSize}
            className="w-full h-full object-contain"
            draggable={false}
            onError={() => setUsePngLogo(false)}
          />
        ) : (
          <svg viewBox="0 0 100 100" className="w-full h-full" aria-hidden>
            <defs>
              <radialGradient id="orb-center" cx="50%" cy="40%" r="60%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="55%" stopColor="#c8e4d2" />
                <stop offset="100%" stopColor="#0e5a37" />
              </radialGradient>
            </defs>
            <circle cx="50" cy="50" r="40" fill="url(#orb-center)" />
          </svg>
        )}
      </div>
    </div>
  );
}
